// "Champion admin": edit what the AI Champions page shows. Edits show on the
// page as they're made and are written to the Champion lists in SharePoint
// when the admin saves.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Info,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  type Champion,
  type ChampionsConfig,
  type CommunityId,
} from "../../data/championsDirectory";
import type { ChampionProfile } from "../../hooks/useChampionDirectory";
import { COMMUNITY_ICONS, Face } from "./parts";

type Tab = "champions" | "communities" | "timeline" | "program";

const EMPTY: Champion = { name: "", community: "land", area: "" };

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="ca-field">
      <span className="ca-field-label">{label}</span>
      {children}
      {hint ? <span className="ca-field-hint">{hint}</span> : null}
    </label>
  );
}

function ChampionForm({
  initial,
  profile,
  config,
  onSave,
  onCancel,
  onPhoto,
}: {
  initial: Champion;
  profile: ChampionProfile | undefined;
  config: ChampionsConfig;
  onSave: (c: Champion) => void;
  onCancel: () => void;
  onPhoto: (previewUrl: string, file: File) => void;
}) {
  const [draft, setDraft] = useState<Champion>(initial);
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const set = <K extends keyof Champion>(k: K, v: Champion[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const isNew = !initial.name;

  // Unchanged, the photo is whatever the page already shows; a newly picked
  // one previews straight away.
  const preview =
    draft.photo === initial.photo ? profile?.photoUrl ?? null : draft.photo?.startsWith("blob:") ? draft.photo : null;

  const save = () => {
    const name = draft.name.trim();
    if (!name) return setError("Add their name.");
    if (config.champions.some((c) => c.name.toLowerCase() === name.toLowerCase() && c.name !== initial.name))
      return setError(`${name} is already a champion.`);
    onSave({
      ...draft,
      name,
      area: draft.area.trim(),
      title: draft.title?.trim() || undefined,
      location: draft.location?.trim() || undefined,
      email: draft.email?.trim() || undefined,
    });
  };

  return (
    <div className="ca-form">
      <button type="button" className="ca-back" onClick={onCancel}>
        <ArrowLeft size={15} aria-hidden /> All champions
      </button>
      <h3 className="ca-form-title">{isNew ? "Add a champion" : `Edit ${initial.name}`}</h3>

      <div className="ca-photo">
        <Face name={draft.name || "?"} photoUrl={preview} size={72} />
        <div className="ca-photo-actions">
          <button type="button" className="ca-btn" onClick={() => file.current?.click()}>
            <Upload size={14} aria-hidden /> Upload photo
          </button>
          {draft.photo ? (
            <button type="button" className="ca-btn ca-btn--ghost" onClick={() => set("photo", undefined)}>
              Use their Microsoft 365 photo
            </button>
          ) : (
            <span className="ca-field-hint">Using their Microsoft 365 photo, when there is one.</span>
          )}
          <input
            ref={file}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                const url = URL.createObjectURL(f);
                onPhoto(url, f);
                set("photo", url);
              }
              e.target.value = "";
            }}
          />
        </div>
      </div>

      <div className="ca-grid">
        <Field label="Name">
          <input value={draft.name} onChange={(e) => set("name", e.target.value)} autoFocus={isNew} />
        </Field>
        <Field label="Community">
          <select value={draft.community} onChange={(e) => set("community", e.target.value as CommunityId)}>
            {config.communities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Area" hint="Shown as a tag, e.g. Land Development or Marketing.">
          <input value={draft.area} onChange={(e) => set("area", e.target.value)} />
        </Field>
        <Field label="Title">
          <input
            value={draft.title ?? ""}
            onChange={(e) => set("title", e.target.value)}
            placeholder={profile?.title && !initial.title ? profile.title : "From Microsoft 365"}
          />
        </Field>
        <Field label="Location">
          <input
            value={draft.location ?? ""}
            onChange={(e) => set("location", e.target.value)}
            placeholder={profile?.location && !initial.location ? profile.location : "From Microsoft 365"}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={draft.email ?? ""}
            onChange={(e) => set("email", e.target.value)}
            placeholder={profile?.email && !initial.email ? profile.email : "From Microsoft 365"}
          />
        </Field>
      </div>
      <p className="ca-note">Leave title, location or email blank to use what's in their Microsoft 365 profile.</p>
      {error ? (
        <p className="ca-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="ca-form-actions">
        <button type="button" className="ca-btn ca-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="ca-btn ca-btn--primary" onClick={save}>
          {isNew ? "Add champion" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function ChampionsTab({
  config,
  people,
  onChange,
  onPhoto,
}: {
  config: ChampionsConfig;
  people: ChampionProfile[];
  onChange: (c: ChampionsConfig) => void;
  onPhoto: (previewUrl: string, file: File) => void;
}) {
  const [query, setQuery] = useState("");
  const [community, setCommunity] = useState<CommunityId | "all">("all");
  const [editing, setEditing] = useState<Champion | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people.filter(
      (p) =>
        (community === "all" || p.community === community) &&
        (!q || [p.name, p.title, p.area, p.location].some((v) => v?.toLowerCase().includes(q)))
    );
  }, [people, query, community]);

  if (editing)
    return (
      <ChampionForm
        initial={editing}
        profile={people.find((p) => p.name === editing.name)}
        config={config}
        onCancel={() => setEditing(null)}
        onPhoto={onPhoto}
        onSave={(c) => {
          const champions = editing.name
            ? config.champions.map((x) => (x === editing ? c : x))
            : [...config.champions, c];
          onChange({
            ...config,
            champions,
            leads: config.leads.map((l) => (l === editing.name ? c.name : l)),
          });
          setEditing(null);
        }}
      />
    );

  const communityName = (id: CommunityId) => config.communities.find((c) => c.id === id)?.name ?? id;

  return (
    <>
      <div className="ca-toolbar">
        <label className="ca-search">
          <Search size={14} aria-hidden />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search champions" aria-label="Search champions" />
        </label>
        <select value={community} onChange={(e) => setCommunity(e.target.value as CommunityId | "all")} aria-label="Filter by community">
          <option value="all">All communities</option>
          {config.communities.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button type="button" className="ca-btn ca-btn--primary" onClick={() => setEditing({ ...EMPTY, community: community === "all" ? "land" : community })}>
          <Plus size={14} aria-hidden /> Add
        </button>
      </div>
      <p className="ca-count">
        {shown.length} of {people.length} champions
      </p>
      <ul className="ca-list">
        {shown.map((p) => (
          <li key={p.name} className="ca-row">
            <Face name={p.name} photoUrl={p.photoUrl} size={34} />
            <div className="ca-row-text">
              <span className="ca-row-name">{p.name}</span>
              <span className="ca-row-meta">{[p.title, communityName(p.community)].filter(Boolean).join(" · ")}</span>
            </div>
            {removing === p.name ? (
              <span className="ca-confirm">
                Remove?
                <button
                  type="button"
                  className="ca-btn ca-btn--danger"
                  onClick={() => {
                    onChange({
                      ...config,
                      champions: config.champions.filter((c) => c.name !== p.name),
                      leads: config.leads.filter((l) => l !== p.name),
                    });
                    setRemoving(null);
                  }}
                >
                  Remove
                </button>
                <button type="button" className="ca-btn ca-btn--ghost" onClick={() => setRemoving(null)}>
                  Keep
                </button>
              </span>
            ) : (
              <span className="ca-row-actions">
                <button
                  type="button"
                  className="ca-icon-btn"
                  onClick={() => setEditing(config.champions.find((c) => c.name === p.name) ?? null)}
                  aria-label={`Edit ${p.name}`}
                >
                  <Pencil size={15} aria-hidden />
                </button>
                <button type="button" className="ca-icon-btn" onClick={() => setRemoving(p.name)} aria-label={`Remove ${p.name}`}>
                  <Trash2 size={15} aria-hidden />
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function CommunitiesTab({ config, onChange }: { config: ChampionsConfig; onChange: (c: ChampionsConfig) => void }) {
  const update = (id: CommunityId, patch: Partial<ChampionsConfig["communities"][number]>) =>
    onChange({ ...config, communities: config.communities.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  return (
    <ul className="ca-cards">
      {config.communities.map((c) => {
        const Icon = COMMUNITY_ICONS[c.id];
        const count = config.champions.filter((x) => x.community === c.id).length;
        return (
          <li key={c.id} className={`ca-card ch-t-${c.id}`}>
            <div className="ca-card-head">
              <span className="ch-tile" aria-hidden>
                <Icon size={16} />
              </span>
              <span className="ca-card-count">{count} champions</span>
            </div>
            <Field label="Name">
              <input value={c.name} onChange={(e) => update(c.id, { name: e.target.value })} />
            </Field>
            <Field label="What AI could look like here" hint="One example per line. Shown in the community popup.">
              <textarea
                rows={4}
                value={c.examples.join("\n")}
                onChange={(e) => update(c.id, { examples: e.target.value.split("\n") })}
                onBlur={() => update(c.id, { examples: c.examples.map((x) => x.trim()).filter(Boolean) })}
              />
            </Field>
          </li>
        );
      })}
    </ul>
  );
}

function TimelineTab({
  config,
  perCommunity,
  onChange,
}: {
  config: ChampionsConfig;
  perCommunity: boolean;
  onChange: (c: ChampionsConfig) => void;
}) {
  const set = (i: number, patch: Partial<ChampionsConfig["milestones"][number]>) =>
    onChange({ ...config, milestones: config.milestones.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  return (
    <>
      <p className="ca-note">
        Each community sees its own milestones plus the ones for every community, in date order, in its popup on the
        page. Past ones are ticked off and the next one is marked "Up next".
      </p>
      {!perCommunity ? (
        <p className="ca-note ca-note--warn">
          To set milestones per community, re-run scripts/sharepoint/create-champion-lists.js on the SharePoint site. Until
          then every milestone shows for every community.
        </p>
      ) : null}
      <ul className="ca-milestones">
        {config.milestones.map((m, i) => (
          <li key={i} className="ca-milestone">
            <input type="date" value={m.date} onChange={(e) => set(i, { date: e.target.value })} aria-label="Date" />
            <input value={m.text} onChange={(e) => set(i, { text: e.target.value })} aria-label="What happens" />
            {perCommunity ? (
              <select
                value={m.community ?? ""}
                onChange={(e) => set(i, { community: (e.target.value || undefined) as CommunityId | undefined })}
                aria-label="Community"
              >
                <option value="">Every community</option>
                {config.communities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              className="ca-icon-btn"
              onClick={() => onChange({ ...config, milestones: config.milestones.filter((_, j) => j !== i) })}
              aria-label="Remove milestone"
            >
              <Trash2 size={15} aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="ca-btn"
        onClick={() =>
          onChange({
            ...config,
            milestones: [...config.milestones, { date: new Date().toISOString().slice(0, 10), text: "" }],
          })
        }
      >
        <Plus size={14} aria-hidden /> Add milestone
      </button>
    </>
  );
}

function ProgramTab({ config, onChange }: { config: ChampionsConfig; onChange: (c: ChampionsConfig) => void }) {
  const names = config.champions.map((c) => c.name).sort((a, b) => a.localeCompare(b));
  const setLead = (i: number, name: string) => {
    const leads = [...config.leads];
    leads[i] = name;
    onChange({ ...config, leads: leads.filter(Boolean) });
  };
  return (
    <div className="ca-stack">
      <Field label="Headline">
        <input value={config.headline} onChange={(e) => onChange({ ...config, headline: e.target.value })} />
      </Field>
      <Field label="Introduction">
        <textarea rows={3} value={config.lede} onChange={(e) => onChange({ ...config, lede: e.target.value })} />
      </Field>
      <Field label="AI Journey survey link">
        <input type="url" value={config.surveyUrl} onChange={(e) => onChange({ ...config, surveyUrl: e.target.value })} />
      </Field>
      <Field
        label="Leave out of leaderboards"
        hint="One email or name per line. They won't appear in Top contributors or count towards the Champions Cup, but still see their own level and badges. Useful for the people who run the program."
      >
        <textarea
          rows={3}
          value={config.leaderboardExclude.join("\n")}
          placeholder="e.g. Suhi.Siva@brookfieldrp.com"
          onChange={(e) => onChange({ ...config, leaderboardExclude: e.target.value.split("\n") })}
        />
      </Field>
      <fieldset className="ca-fieldset">
        <legend className="ca-field-label">Program leads</legend>
        <span className="ca-field-hint">Shown under "Want to become an AI Champion?"</span>
        {[0, 1, 2].map((i) => (
          <select key={i} value={config.leads[i] ?? ""} onChange={(e) => setLead(i, e.target.value)} aria-label={`Lead ${i + 1}`}>
            <option value="">None</option>
            {names.map((n) => (
              <option key={n} value={n} disabled={config.leads.includes(n) && config.leads[i] !== n}>
                {n}
              </option>
            ))}
          </select>
        ))}
      </fieldset>
    </div>
  );
}

export type SaveState = { status: "idle" | "saving" | "saved" | "error"; message?: string };

export function ChampionAdmin({
  config,
  people,
  dirty,
  canSave,
  perCommunityTimeline,
  save,
  onChange,
  onSave,
  onDiscard,
  onClose,
}: {
  config: ChampionsConfig;
  people: ChampionProfile[];
  /** The page is showing edits that aren't saved yet. */
  dirty: boolean;
  /** False when the SharePoint lists couldn't be loaded. */
  canSave: boolean;
  /** Milestones can be set per community (the list has its Community column). */
  perCommunityTimeline: boolean;
  save: SaveState;
  onChange: (c: ChampionsConfig) => void;
  onSave: (photos: Map<string, File>) => void;
  onDiscard: () => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>("champions");
  const closeRef = useRef<HTMLButtonElement>(null);
  const photos = useRef(new Map<string, File>());
  const saving = save.status === "saving";

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "champions", label: "Champions", count: config.champions.length },
    { id: "communities", label: "Communities", count: config.communities.length },
    { id: "timeline", label: "Timeline", count: config.milestones.length },
    { id: "program", label: "Program" },
  ];

  return (
    <div className="ca-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="ca-drawer" role="dialog" aria-modal="true" aria-labelledby="ca-title">
        <div className="ca-head">
          <span className="ca-head-icon" aria-hidden>
            <Settings2 size={18} />
          </span>
          <div className="ca-head-text">
            <h2 id="ca-title" className="ca-title">
              Champion admin
            </h2>
            <p className="ca-sub">Edits show on the page as you make them. Save to publish them for everyone.</p>
          </div>
          <button ref={closeRef} type="button" className="ch-close" onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </div>
        {!canSave ? (
          <p className="ca-status ca-status--warn">
            <AlertTriangle size={14} aria-hidden />
            <span>Couldn't reach the Champion lists in SharePoint, so changes can't be saved right now.</span>
          </p>
        ) : save.status === "error" ? (
          <p className="ca-status ca-status--warn" role="alert">
            <AlertTriangle size={14} aria-hidden />
            <span>
              <b>Not everything saved.</b> {save.message}
            </span>
          </p>
        ) : saving ? (
          <p className="ca-status" aria-live="polite">
            <Loader2 size={14} className="ca-spin" aria-hidden />
            <span>{save.message ?? "Saving…"}</span>
          </p>
        ) : dirty ? (
          <p className="ca-status ca-status--draft">
            <Info size={14} aria-hidden />
            <span>
              <b>Unsaved changes.</b> Only you can see them until you save.
            </span>
          </p>
        ) : save.status === "saved" ? (
          <p className="ca-status ca-status--ok" aria-live="polite">
            <Check size={14} aria-hidden />
            <span>Saved to SharePoint.</span>
          </p>
        ) : null}
        <div className="ca-tabs" role="tablist" aria-label="Champion admin sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`ca-tab${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.count !== undefined ? <span className="ca-tab-count">{t.count}</span> : null}
            </button>
          ))}
        </div>
        <div className="ca-body" role="tabpanel">
          {tab === "champions" ? (
            <ChampionsTab
              config={config}
              people={people}
              onChange={onChange}
              onPhoto={(url, file) => photos.current.set(url, file)}
            />
          ) : tab === "communities" ? (
            <CommunitiesTab config={config} onChange={onChange} />
          ) : tab === "timeline" ? (
            <TimelineTab config={config} perCommunity={perCommunityTimeline} onChange={onChange} />
          ) : (
            <ProgramTab config={config} onChange={onChange} />
          )}
        </div>
        <div className="ca-foot">
          <button type="button" className="ca-btn ca-btn--ghost" onClick={onDiscard} disabled={!dirty || saving}>
            <RotateCcw size={14} aria-hidden /> Discard changes
          </button>
          <span className="ca-foot-actions">
            <button type="button" className="ca-btn" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="ca-btn ca-btn--primary"
              onClick={() => onSave(photos.current)}
              disabled={!dirty || !canSave || saving}
            >
              {saving ? <Loader2 size={14} className="ca-spin" aria-hidden /> : null} Save to SharePoint
            </button>
          </span>
        </div>
      </aside>
    </div>
  );
}
